import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useId, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { keys } from '../../api/keys'
import { emit } from '../../platform/analytics/analytics'
import { Async } from '../../platform/async/Async'
import { useToast } from '../../platform/feedback/ToastProvider'
import { DisabledReason } from '../../platform/access/DisabledReason'
import { Skeleton } from '../../platform/async/Skeleton'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { Button } from '../../platform/primitives/Button'
import { Panel } from '../../platform/primitives/Panel'
import { SearchField } from '../../platform/primitives/SearchField'
import { SegmentedControl } from '../../platform/primitives/SegmentedControl'
import { ApplyAllDialog } from './ApplyAllDialog'
import { appliedAllMessage } from './applyCopy'
import { DeleteRuleDialog } from './DeleteRuleDialog'
import { useCategories } from '../categories/useCategories'
import { suggestionDraft } from './builderState'
import { COUNTING, useRuleFlow } from './RuleFlow'
import { dropEdge } from './dropEdge'
import { RuleSuggestions } from './RuleSuggestions'
import { RuleRow, rowLayoutFor, summaryOf, type RowLayout, type RowOrder } from './RuleRow'
import type { ReadRule } from './ruleWire'
import type { AppliedAll } from './useRuleApply'
import { useDismissSuggestion, useReorderRules, useRuleSuggestions, type RuleSuggestion } from './useRules'
import { useViewedOnce } from './useViewedOnce'
import './rules.css'

/** The two tabs' routes. `/rules/suggestions` is declared beside the generated
 *  `/rules/*` in App.tsx (`RULE_SUGGESTIONS_PATH`); keep the two in step. */
const TAB_PATH = { rules: '/rules', suggestions: '/rules/suggestions' } as const
type Tab = keyof typeof TAB_PATH

const CAP_REASON_ID = 'rules-cap-reason'

export function Rules({ tab }: { tab: Tab }) {
  const navigate = useNavigate()
  const [deleting, setDeleting] = useState<ReadRule | null>(null)
  const [applyingAll, setApplyingAll] = useState(false)
  const toast = useToast()
  // Builder, save, switch and Undo: see `useRuleFlow`.
  // Where focus goes when a delete or pause unmounts the dialog, the builder
  // and the row that opened them (#504). The heading is always rendered, even
  // when the list is empty; the in-list "New rule" button is not.
  const headingRef = useRef<HTMLHeadingElement>(null)
  const flow = useRuleFlow({ onDelete: setDeleting, restoreFocusTo: headingRef })
  const { state, names, saver, all } = flow
  // `Rules` also hosts the Suggestions tab, which reports for itself.
  useViewedOnce(state, 'rules', tab === 'rules')
  const reorder = useReorderRules()
  const client = useQueryClient()
  const breakpoint = useBreakpoint()
  const newRowId = useId()
  const canDrag = breakpoint === 'desktop' || breakpoint === 'wide'
  const [dragId, setDragId] = useState<string | null>(null)
  const [overId, setOverId] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const suggestions = useRuleSuggestions()
  const dismiss = useDismissSuggestion()

  const categories = useCategories()
  const createFrom = (s: RuleSuggestion) => {
    // Only a LOADED list can say a category is gone; until then keep the prefill.
    const loaded = categories.status === 'success' || categories.status === 'stale' ? categories.data : null
    const gone = loaded !== null && !loaded.some((c) => c.id === s.categoryId)
    const draft = suggestionDraft(s)
    // The suggestion is used once its rule is WRITTEN, whether or not the user
    // went on to apply it to the past. `afterSave` says the rule is safe if
    // the dismiss itself fails.
    const used = () => {
      emit('rule_suggestion_used')
      dismiss.mutate({ id: s.id, afterSave: true })
    }
    flow.open(
      'new',
      gone ? { ...draft, actions: [{ type: 'set_category', value: '' }] } : draft,
      // Fully saved (any backfill included). `used` also runs as `onWrittenUncounted` (the name saveDirect uses)
      // for a rule written and then left; the two are mutually exclusive.
      used,
      {
        onWrittenUncounted: used,
        notice: gone ? `The category “${s.categoryName}” no longer exists. Choose another to continue.` : undefined,
      },
    )
  }

  /** Every reorder — button or drop — comes through here. The order is built
   *  from the CACHE at call time, not from props: a second move while the
   *  first is in flight must build on the first. `method` rides to the
   *  reorder hook, which emits once the server has accepted the order. */
  const move = useCallback(
    (build: (ids: string[]) => string[] | null, method: 'buttons' | 'drag') => {
      const current = client.getQueryData<ReadRule[]>(keys.rules.list())
      if (current === undefined) return
      const ids = build(current.map((r) => r.rule.id))
      if (ids === null) return
      reorder.mutate({ ids, method })
    },
    [client, reorder],
  )

  const shift = useCallback(
    (id: string, by: -1 | 1) =>
      move((ids) => {
        const i = ids.indexOf(id)
        const j = i + by
        if (i < 0 || j < 0 || j >= ids.length) return null
        const next = [...ids]
        next.splice(j, 0, ...next.splice(i, 1))
        return next
      }, 'buttons'),
    [move],
  )

  const drop = useCallback(
    (from: string, onto: string) =>
      move((ids) => {
        const i = ids.indexOf(from)
        const j = ids.indexOf(onto)
        if (i < 0 || j < 0 || i === j) return null
        const next = [...ids]
        next.splice(j, 0, ...next.splice(i, 1))
        return next
      }, 'drag'),
    [move],
  )

  const active = all?.filter((r) => r.rule.is_enabled).length ?? 0

  /** Each Apply all run's toast, with Undo of that run's batch when it changed anything. */
  const reportAll = (result: AppliedAll) =>
    toast.show({
      kind: 'success',
      message: appliedAllMessage(result),
      ...(result.modified > 0 ? { action: { label: 'Undo', onAct: () => saver.offerUndo(result.batchId) } } : {}),
    })
  // The design's seven columns start at desktop (1024px); below it the THEN column
  // folds under the conditions, and below 640px the row is a card.
  const layout: RowLayout = rowLayoutFor(breakpoint)
  // A tab says how many it holds once that is known (R22). An empty list is a
  // known zero; a loading or failed one is not, and shows the bare word.
  const { atCap, reason: capReason } = flow.cap
  const ruleCount = state.status === 'empty' ? 0 : all?.length
  const suggestionCount =
    suggestions.status === 'success' ? suggestions.data.length : suggestions.status === 'empty' ? 0 : undefined

  return (
    <>
      <div className="rules-head">
        <div className="rules-head__title">
          <h2 className="rules-head__h" ref={headingRef} tabIndex={-1}>
            Auto-rules
          </h2>
          {all !== undefined && (
            <p className="rules-head__count">
              {active} active · {all.length} {all.length === 1 ? 'rule' : 'rules'}
            </p>
          )}
        </div>
        <div className="rules-head__actions">
          {/* Only while some rule is on: a paused rule never applies, so with none on there is nothing to run. */}
          {active > 0 && (
            <Button variant="outline" size="page" onClick={() => setApplyingAll(true)}>
              Apply all rules…
            </Button>
          )}
          {atCap ? (
            <DisabledReason reason={capReason} reasonId={CAP_REASON_ID}>
              <Button variant="primary" size="page" aria-disabled="true" aria-describedby={CAP_REASON_ID}>
                ＋ New rule
              </Button>
            </DisabledReason>
          ) : (
            <Button variant="primary" size="page" onClick={() => flow.open('new')}>
              ＋ New rule
            </Button>
          )}
        </div>
      </div>
      <div className="rules-tools">
        <SegmentedControl
          label="Auto-rules view"
          name="rules-tab"
          options={[
            { value: 'rules', label: 'Rules', count: ruleCount },
            { value: 'suggestions', label: 'Suggestions', count: suggestionCount },
          ]}
          value={tab}
          onChange={(next) => navigate(TAB_PATH[next])}
        />
        {tab === 'rules' && (
          <SearchField label="Filter rules" value={query} onChange={setQuery} placeholder="Filter rules" />
        )}
      </div>
      {tab === 'rules' ? (
        <>
          <div className="rules-note">
            <span className="rules-note__badge" aria-hidden="true">
              1st
            </span>
            <div className="rules-note__text">
              <span className="rules-note__title">The first matching rule wins</span>
              <span className="rules-note__sub">
                Rules run top to bottom and stop at the first match.{' '}
                {canDrag ? 'Drag ⋮⋮ to change priority.' : 'Use the arrows to change priority.'}
              </span>
            </div>
          </div>
          {suggestions.status === 'success' && suggestions.data.length > 0 && (
            <div className="rules-banner">
              <span className="rules-banner__dot" aria-hidden="true" />
              <span className="rules-banner__text">
                {suggestions.data.length} rule {suggestions.data.length === 1 ? 'suggestion' : 'suggestions'} from
                your uncategorised transactions
              </span>
              <InlineLink to={TAB_PATH.suggestions}>Review →</InlineLink>
            </div>
          )}
          {/* The switch's dry run (M11-R11). The builder shows its own. */}
          <p className="rules-status" role="status">
            {flow.counting ? COUNTING : ''}
          </p>
          <Panel className="rules-panel">
            <Async
              surface="rules"
              state={state}
              skeleton={
                <>
                  <Skeleton height={72} />
                  <Skeleton height={72} />
                  <Skeleton height={72} />
                </>
              }
              empty={{
                title: 'No rules yet',
                action: { label: 'New rule', onAct: () => flow.open('new') },
              }}
            >
              {(rules) => {
                const q = query.trim().toLowerCase()
                // Position is the real priority rank, taken BEFORE filtering.
                const shown = rules
                  .map((read, i) => ({ read, position: i + 1 }))
                  .filter(
                    ({ read }) =>
                      q === '' ||
                      `${read.rule.name} ${summaryOf(read, names)}`.toLowerCase().includes(q),
                  )
                const none = shown.length === 0 && (
                  <div className="rules-none">
                    <span className="rules-none__title">No rule matches “{query.trim()}”</span>
                    <span className="note">Try a merchant, a description or a category.</span>
                  </div>
                )
                return (
                  <div className="rules-table">
                  {/* A visual row only (R18): the list stays an <ol>, and every
                      cell below already names itself. */}
                  {layout !== 'phone' && (
                    <div className={`rules-cols rules-cols--${layout}`} aria-hidden="true">
                      <span className="rules-cols__pos">#</span>
                      <span className="rules-cols__when">{layout === 'wide' ? 'Rule · when' : 'Rule · when → then'}</span>
                      {layout === 'wide' && <span className="rules-cols__then">Then</span>}
                      <span className="rules-cols__applied">Applied</span>
                      <span className="rules-cols__on">On</span>
                    </div>
                  )}
                  {none}
                  {shown.length > 0 && (
                  <ol className="rules-list">
                    {shown.map(({ read, position }) => {
                      const id = read.rule.id
                      // While filtering, the visible neighbours are not the
                      // real ones, so the controls are not offered at all.
                      const order: RowOrder | undefined =
                        q !== ''
                          ? undefined
                          : {
                              isFirst: position === 1,
                              isLast: position === rules.length,
                              onMoveUp: () => shift(id, -1),
                              onMoveDown: () => shift(id, 1),
                              drag: canDrag
                                ? {
                                    dragging: dragId === id,
                                    over: overId === id && dragId !== null ? dropEdge(rules.map((r) => r.rule.id), dragId, id) : null,
                                    onDragStart: (e) => {
                                      e.dataTransfer.setData('text/plain', id)
                                      e.dataTransfer.effectAllowed = 'move'
                                      setDragId(id)
                                    },
                                    onDragOver: (e) => {
                                      e.preventDefault()
                                      e.dataTransfer.dropEffect = 'move'
                                      setOverId(id)
                                    },
                                    onDrop: (e) => {
                                      e.preventDefault()
                                      const from = dragId ?? e.dataTransfer.getData('text/plain')
                                      setDragId(null)
                                      setOverId(null)
                                      if (from !== '') drop(from, id)
                                    },
                                    onDragEnd: () => {
                                      setDragId(null)
                                      setOverId(null)
                                    },
                                  }
                                : undefined,
                            }
                      return (
                      <RuleRow
                        key={read.rule.id}
                        read={read}
                        position={position}
                        names={names}
                        onEdit={() => flow.open(read)}
                        onToggle={() => (read.rule.is_enabled ? saver.pause(read) : void saver.enable(read))}
                        layout={layout}
                        switching={saver.switching.has(read.rule.id)}
                        onDelete={() => setDeleting(read)}
                        order={order}
                      />
                      )
                    })}
                  </ol>
                  )}
                  <button
                    type="button"
                    className="rules-new"
                    aria-labelledby={`${newRowId}-title`}
                    aria-describedby={`${newRowId}-hint`}
                    aria-disabled={atCap || undefined}
                    onClick={() => flow.open('new')}
                  >
                    <span className="rules-new__plus" aria-hidden="true">
                      ＋
                    </span>
                    <span className="rules-new__title" id={`${newRowId}-title`}>
                      New rule
                    </span>
                    <span className="rules-new__hint" id={`${newRowId}-hint`}>
                      {atCap ? capReason : 'Match on merchant, description, account, date or note.'}
                    </span>
                  </button>
                  </div>
                )
              }}
            </Async>
            {state.status === 'empty' && (
              <div className="rules-none rules-none--action">
                <Button variant="outline" size="panel" onClick={() => navigate(TAB_PATH.suggestions)}>
                  See suggestions
                </Button>
              </div>
            )}
          </Panel>
        </>
      ) : (
        <RuleSuggestions state={suggestions} onCreate={createFrom} blocked={atCap ? capReason : undefined} />
      )}
      {flow.element}
      {applyingAll && <ApplyAllDialog onClose={() => setApplyingAll(false)} onRun={reportAll} />}
      {deleting !== null && (
        <DeleteRuleDialog
          read={deleting}
          names={names}
          restoreFocusTo={headingRef}
          onClose={() => setDeleting(null)}
          onDone={() => {
            // A delete or pause from the builder makes its edit moot.
            flow.afterDelete()
            setDeleting(null)
          }}
        />
      )}
    </>
  )
}
